import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { profileAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { User, Building2, Save, Star } from 'lucide-react';

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const SERVICES = [
  "Holzernte", "Rückung", "Aufforstung", "Waldpflege",
  "Harvesterarbeiten", "Manuelle Fällung", "Forwarder",
  "Pflanzung", "Durchforstung", "Zaunbau"
];

const ProfilePage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  const isAnbieter = user?.role === 'anbieter';

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await profileAPI.getMyProfile();
      setProfile(response.data);
      setFormData(response.data);
    } catch {
      toast.error(t('profil.ladeFehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    const services = formData.services || [];
    setFormData(prev => ({
      ...prev,
      services: services.includes(service)
        ? services.filter(s => s !== service)
        : [...services, service]
    }));
  };

  const toggleRegion = (region) => {
    const regions = formData.service_regions || [];
    setFormData(prev => ({
      ...prev,
      service_regions: regions.includes(region)
        ? regions.filter(r => r !== region)
        : [...regions, region]
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await profileAPI.updateMyProfile(formData);
      toast.success(t('profil.gespeichert'));
      fetchProfile();
    } catch {
      toast.error(t('profil.speichernFehler'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-primary">{t('profil.wirdGespeichert').replace('...', '')}...</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              {t('profil.titel')}
            </h1>
            <p className="text-gray-500 mt-1">
              {isAnbieter ? t('profil.anbieterProfil') : t('profil.waldbesitzerProfil')}
            </p>
          </div>

          <Badge className={isAnbieter ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary'}>
            {isAnbieter ? 'Anbieter' : 'Waldbesitzer'}
          </Badge>
        </div>

        {/* Profile Stats */}
        {profile && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  {isAnbieter && formData.company_name ? (
                    <Building2 className="h-10 w-10 text-primary" />
                  ) : (
                    <User className="h-10 w-10 text-primary" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {formData.company_name || user?.name}
                  </h2>
                  <p className="text-gray-500">{user?.email}</p>
                  {profile.avg_rating > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-medium">{profile.avg_rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profil.grunddaten')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAnbieter && (
              <div className="space-y-2">
                <Label>{t('profil.firmenname')}</Label>
                <Input
                  value={formData.company_name || ''}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  data-testid="input-company-name"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('profil.bundesland')}</Label>
                <Select
                  value={formData.bundesland || ''}
                  onValueChange={(v) => handleChange('bundesland', v)}
                >
                  <SelectTrigger data-testid="select-bundesland">
                    <SelectValue placeholder={t('profil.auswahl')} />
                  </SelectTrigger>
                  <SelectContent>
                    {BUNDESLAENDER.map((land) => (
                      <SelectItem key={land} value={land}>{land}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('profil.ort')}</Label>
                <Input
                  value={formData.ort || ''}
                  onChange={(e) => handleChange('ort', e.target.value)}
                  data-testid="input-ort"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('profil.telefon')}</Label>
              <Input
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+49 ..."
                data-testid="input-phone"
              />
            </div>

            {isAnbieter && (
              <div className="space-y-2">
                <Label>{t('profil.website')}</Label>
                <Input
                  value={formData.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="https://..."
                  data-testid="input-website"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t('profil.beschreibung')}</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder={isAnbieter
                  ? t('profil.beschreibungPlaceholderAnbieter')
                  : t('profil.beschreibungPlaceholderWaldbesitzer')}
                rows={4}
                data-testid="input-description"
              />
            </div>

            {isAnbieter && (
              <div className="space-y-2">
                <Label>{t('profil.erfahrungJahre')}</Label>
                <Input
                  type="number"
                  value={formData.experience_years || ''}
                  onChange={(e) => handleChange('experience_years', parseInt(e.target.value) || null)}
                  data-testid="input-experience"
                />
              </div>
            )}

            {/* Einsatzradius (Anbieter) */}
            {isAnbieter && (
              <div className="space-y-2">
                <Label>{t('profil.einsatzradius')}</Label>
                <p className="text-xs text-muted-foreground">{t('profil.einsatzradiusHinweis')}</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="10"
                    max="500"
                    step="10"
                    value={formData.service_radius_km || ''}
                    onChange={(e) => handleChange('service_radius_km', parseInt(e.target.value) || null)}
                    className="w-32"
                    data-testid="input-radius"
                  />
                  <span className="text-sm text-muted-foreground">km</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Services (Anbieter only) */}
        {isAnbieter && (
          <Card>
            <CardHeader>
              <CardTitle>{t('profil.dienstleistungen')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SERVICES.map((service) => (
                  <div
                    key={service}
                    onClick={() => toggleService(service)}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      (formData.services || []).includes(service)
                        ? 'bg-accent/10 border-accent'
                        : 'border-warm-200 hover:border-accent/50'
                    }`}
                  >
                    <Checkbox checked={(formData.services || []).includes(service)} />
                    <span className="text-sm">{service}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Regions (Anbieter only) */}
        {isAnbieter && (
          <Card>
            <CardHeader>
              <CardTitle>{t('profil.einsatzgebiete')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {BUNDESLAENDER.map((region) => (
                  <div
                    key={region}
                    onClick={() => toggleRegion(region)}
                    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                      (formData.service_regions || []).includes(region)
                        ? 'bg-primary/10 border-primary'
                        : 'border-warm-200 hover:border-primary/50'
                    }`}
                  >
                    <Checkbox checked={(formData.service_regions || []).includes(region)} />
                    {region}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-primary hover:bg-primary-600"
            data-testid="save-profile-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? t('profil.wirdGespeichert') : t('profil.aenderungenSpeichern')}
          </Button>
        </div>
      </div>
    </AppShell>
  );
};

export default ProfilePage;
